// app/javascript/controllers/wiki_tree_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["disclosure"]

    connect() {
        this.toggleHandlers = new WeakMap()

        this.disclosureTargets.forEach(d => {
            const id = d.dataset.wikiTreeIdValue
            if (!id) return

            const stored = localStorage.getItem(`wiki_tree_${id}`)
            if (stored === 'true') d.open = true
            else if (stored === 'false') d.open = false

            const handler = () => {
                localStorage.setItem(`wiki_tree_${id}`, d.open)
            }
            d.addEventListener('toggle', handler)
            this.toggleHandlers.set(d, handler)
        })

        // Reconcile the current-page highlight against the live URL. This corrects
        // Turbo cached-preview renders (which carry the prior page's `--current`)
        // and removes the optimistic class from elements that no longer match.
        this.reconcileCurrent()

        // Enable transitions only after first paint so chevrons don't animate
        // from 0° to 90° when an already-open <details> first renders.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.element.classList.add('wiki-tree-ready')
            })
        })
    }

    disconnect() {
        this.disclosureTargets.forEach(d => {
            const handler = this.toggleHandlers?.get(d)
            if (handler) d.removeEventListener('toggle', handler)
        })
        this.element.classList.remove('wiki-tree-ready')
    }

    stopToggle(event) {
        event.stopPropagation()
    }

    activatePage(event) {
        if (event.target.closest('.wiki-tree-actions, .wiki-tree-title-input')) return

        const li = event.currentTarget
        const link = li.querySelector('a.wiki-tree-title')
        if (!link) return

        this.setCurrent(li)

        if (!event.target.closest('a.wiki-tree-title')) {
            event.preventDefault()
            link.click()
        }
    }

    reconcileCurrent() {
        const path = window.location.pathname
        const matchingLink = Array.from(this.element.querySelectorAll('li.wiki-tree-page a.wiki-tree-title'))
            .find(a => new URL(a.href).pathname === path)
        const matchingLi = matchingLink?.closest('li.wiki-tree-page')

        this.element.querySelectorAll('.wiki-tree-page--current').forEach(prev => {
            if (prev === matchingLi) return
            prev.classList.remove('wiki-tree-page--current')
            prev.querySelector('[aria-current="page"]')?.removeAttribute('aria-current')
        })

        if (matchingLi) {
            matchingLi.classList.add('wiki-tree-page--current')
            matchingLink.setAttribute('aria-current', 'page')
        }
    }

    setCurrent(li) {
        this.element.querySelectorAll('.wiki-tree-page--current').forEach(prev => {
            if (prev === li) return
            prev.classList.remove('wiki-tree-page--current')
            prev.querySelector('[aria-current="page"]')?.removeAttribute('aria-current')
        })

        li.classList.add('wiki-tree-page--current')
        const link = li.querySelector('a.wiki-tree-title')
        if (link) link.setAttribute('aria-current', 'page')
    }
}
